import {
  Button,
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { formatIsoDateTimeDisplay, useTranslation } from "@atlas/sdk";
import {
  type IHubExport,
  useGenerateHubExportToken,
  useHubExports,
} from "@query";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Key,
  Loader2,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { ExpandedExportedItems } from "../components/ExpandedExportedItems";

function GenerateTokenButton({ exportId }: { exportId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isCopied, setIsCopied] = useState(false);

  const { data, isFetching, refetch } = useGenerateHubExportToken(exportId, {
    enabled: false,
  });

  const token =
    typeof data === "string"
      ? data
      : (data as unknown as { data?: string })?.data;

  const handleClick = useCallback(() => {
    if (token) {
      navigator.clipboard.writeText(token);
      setIsCopied(true);
      toast.add({
        title: t("hub.package_manager.token_copied", {
          defaultValue: "Token copied to clipboard!",
        }),
        data: { type: "success" },
      });
      setTimeout(() => setIsCopied(false), 2000);
    } else {
      refetch();
    }
  }, [token, refetch, t, toast]);

  if (token) {
    return (
      <Button variant="ghost" size="sm" onClick={handleClick}>
        {isCopied ? (
          <>
            <Check className="mr-2 size-4 text-success-foreground" />
            {t("hub.export_list.token_copied", {
              defaultValue: "Token Copied",
            })}
          </>
        ) : (
          <>
            <Copy className="mr-2 size-4" />
            {t("hub.export_list.copy_token", { defaultValue: "Copy Token" })}
          </>
        )}
      </Button>
    );
  }

  return (
    <WithToolTip
      tooltipText={t("hub.export_list.generate_token", {
        defaultValue: "Generate token",
      })}>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleClick}
        disabled={isFetching}>
        {isFetching ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Key className="size-4" />
        )}
      </Button>
    </WithToolTip>
  );
}

export function ExportListPage() {
  const { t } = useTranslation();
  const { data: exportsData, isLoading } = useHubExports();

  const sortedExportsData = useMemo(() => {
    if (!exportsData) return [];
    return [...exportsData].sort((a, b) => {
      const dateA = new Date(a.createdDatetime).getTime();
      const dateB = new Date(b.createdDatetime).getTime();
      return dateB - dateA;
    });
  }, [exportsData]);

  const columns = useMemo<ColumnDef<IHubExport>[]>(
    () => [
      {
        id: "expand",
        header: () => null,
        size: 28,
        minSize: 20,
        cell: ({ row }) => {
          return (
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={(e) => {
                e.stopPropagation();
                row.toggleExpanded();
              }}>
              {row.getIsExpanded() ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          );
        },
        meta: {
          expandedContent: (exportItem: IHubExport) => (
            <ExpandedExportedItems exportItem={exportItem} />
          ),
        },
      },
      {
        accessorKey: "exportFeature",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.export_list.type", { defaultValue: "Type" })}
          />
        ),
        filterFn: (row, columnId, filterValue) => {
          const values = Array.isArray(filterValue) ? filterValue : [];
          if (values.length === 0) return true;
          return values.includes(row.getValue(columnId));
        },
        cell: ({ row }) => {
          const feature = row.original.exportFeature;
          return feature === "PACKAGE"
            ? t("hub.export_list.type_package", { defaultValue: "Package" })
            : feature === "CATALOG"
              ? t("hub.export_list.type_catalog", { defaultValue: "Catalog" })
              : feature;
        },
      },
      {
        accessorKey: "createdBy",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.export_list.created_by", {
              defaultValue: "Created By",
            })}
          />
        ),
        cell: ({ row }) => (
          <WithToolTip tooltipText={row.original.createdBy}>
            <div className="max-w-[200px] truncate">
              {row.original.createdBy}
            </div>
          </WithToolTip>
        ),
      },
      {
        accessorKey: "createdDatetime",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.export_list.created_time", {
              defaultValue: "Created Time",
            })}
          />
        ),
        cell: ({ row }) =>
          formatIsoDateTimeDisplay(row.original.createdDatetime),
      },
      {
        id: "actions",
        header: t("hub.export_list.token", { defaultValue: "Token" }),
        cell: ({ row }) => (
          <GenerateTokenButton exportId={row.original.exportId} />
        ),
      },
    ],
    [t],
  );

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex-1 min-h-0">
        <DataGrid
          columns={columns}
          data={sortedExportsData}
          loading={isLoading}
          tableLayout={{
            height: "full",
            headerSticky: true,
          }}
          withFilter={{
            filters: [
              {
                columnId: "exportFeature",
                label: t("hub.export_list.type", { defaultValue: "Type" }),
                options: [
                  {
                    label: t("hub.export_list.type_package", {
                      defaultValue: "Package",
                    }),
                    value: "PACKAGE",
                  },
                  {
                    label: t("hub.export_list.type_catalog", {
                      defaultValue: "Catalog",
                    }),
                    value: "CATALOG",
                  },
                ],
              },
            ],
          }}
          paginationPlacement="toolbar"
          withExpandable
          paginationConfig={{
            showRowsPerPage: true,
            showPageButtons: true,
            showInfo: true,
            showNavigation: true,
            sizes: [10, 20, 30, 50, 100],
          }}
        />
      </div>
    </div>
  );
}
